import { useEffect, useMemo, useState } from "react";
import {
  Megaphone,
  Sparkles,
  ImageIcon,
  Loader2,
  RefreshCw,
  Copy,
  Check,
  Download,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import GenerationProgress from "../components/GenerationProgress.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";
import { chatStream, generateImage, getModels } from "../lib/api.js";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { resolvePromptText } from "../lib/promptText.js";
import { useI18n } from "../lib/i18n.jsx";

const DEFAULT_CHAT_MODEL = "nvidia/nemotron-3.5-lightning-30b-a3b";
const DEFAULT_IMAGE_MODEL = "black-forest-labs/flux.3-image-klein";

const PLATFORM_OPTIONS = [
  "Instagram",
  "TikTok",
  "LinkedIn",
  "X / Twitter",
  "Facebook",
];

const TONE_OPTIONS = ["Professional", "Casual", "Bold", "Friendly", "Luxury"];

function parsePlan(text) {
  const candidates = [text];
  const fenced = text.match(/```json\s*([\s\S]*?)```/i);
  if (fenced?.[1]) candidates.push(fenced[1]);
  const firstBrace = text.indexOf("{");
  const lastBrace = text.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    candidates.push(text.slice(firstBrace, lastBrace + 1));
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // Keep trying fallbacks.
    }
  }
  return null;
}

function buildMarketingPrompt({
  business,
  offer,
  audience,
  platform,
  tone,
  goal,
  notes,
}) {
  return [
    "Create one social media post plan for a business.",
    "Return valid JSON only with keys: caption, hook, cta, hashtags, image_prompt.",
    "hashtags must be an array of 5 to 10 short hashtag strings including #.",
    "",
    `Business: ${business || "Unspecified business"}`,
    `Offer: ${offer || "General offer"}`,
    `Audience: ${audience || "General audience"}`,
    `Platform: ${platform}`,
    `Tone: ${tone}`,
    `Goal: ${goal}`,
    `Extra notes: ${notes || "none"}`,
  ].join("\n");
}

export default function Marketing() {
  const { t } = useI18n();
  const [chatModels, setChatModels] = useState([]);
  const [imageModels, setImageModels] = useState([]);
  const [chatModel, setChatModel] = useState(DEFAULT_CHAT_MODEL);
  const [imageModel, setImageModel] = useState(DEFAULT_IMAGE_MODEL);

  const [business, setBusiness] = useState("");
  const [offer, setOffer] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("Get more leads");
  const [platform, setPlatform] = useState(PLATFORM_OPTIONS[0]);
  const [tone, setTone] = useState(TONE_OPTIONS[0]);
  const [notes, setNotes] = useState("");

  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageSrc, setImageSrc] = useState("");

  const [loadingCopy, setLoadingCopy] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getModels()
      .then((m) => {
        const nextChat = m.chat || [];
        const nextImage = m.image || [];
        setChatModels(nextChat);
        setImageModels(nextImage);
        if (nextChat.length > 0) setChatModel(nextChat[0].id);
        if (nextImage.length > 0) setImageModel(nextImage[0].id);
      })
      .catch(() => {});
  }, []);

  const busy = loadingCopy || loadingImage;
  const canGenerateCopy =
    Boolean(business.trim() || offer.trim() || notes.trim()) && !loadingCopy;
  const canGenerateImage = imagePrompt.trim() && !loadingImage;

  const system = useMemo(
    () =>
      "You are a senior social media strategist. Create concise, conversion-focused posts for businesses.",
    []
  );

  const fullPostText = useMemo(() => {
    const body = String(caption || "").trim();
    const tags = hashtags.filter(Boolean).join(" ").trim();
    return [body, tags].filter(Boolean).join("\n\n");
  }, [caption, hashtags]);

  const generateCopy = async (ideaOverride) => {
    const ideaText = resolvePromptText(ideaOverride, notes);
    const prompt = buildMarketingPrompt({
      business,
      offer,
      audience,
      platform,
      tone,
      goal,
      notes: ideaText,
    });

    setError(null);
    setLoadingCopy(true);
    setStatus(t("marketing.status.copy", "Writing post copy…"));
    let full = "";

    try {
      await chatStream(
        {
          model: chatModel,
          temperature: 0.7,
          max_tokens: 1100,
          messages: [
            { role: "system", content: system },
            { role: "user", content: prompt },
          ],
        },
        {
          onStatus: setStatus,
          onDelta: (_delta, next) => {
            full = next;
          },
        }
      );

      const parsed = parsePlan(full);
      const nextCaption = String(parsed?.caption || full || "").trim();
      const nextHashtags = Array.isArray(parsed?.hashtags)
        ? parsed.hashtags
        : [];
      const nextImagePrompt = String(parsed?.image_prompt || "").trim();

      const finalImagePrompt =
        nextImagePrompt ||
        `Marketing image for ${business || "a local business"} and ${offer || "its offer"}`;
      setCaption(nextCaption);
      setHashtags(nextHashtags);
      setImagePrompt(finalImagePrompt);
      return finalImagePrompt;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoadingCopy(false);
      if (!loadingImage) setStatus("");
    }
  };

  const generatePostImage = async (promptOverride) => {
    const nextPrompt = resolvePromptText(promptOverride, imagePrompt);
    if (!nextPrompt.trim()) return false;
    setError(null);
    setLoadingImage(true);
    setStatus(t("marketing.status.image", "Generating post image…"));

    try {
      const res = await generateImage(
        {
          model: imageModel,
          prompt: nextPrompt.trim(),
          width: 1024,
          height: 1024,
        },
        { onStatus: setStatus }
      );
      setImageSrc(res.image || "");
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoadingImage(false);
      if (!loadingCopy) setStatus("");
    }
  };

  const generateAll = async (notesOverride) => {
    if (busy) return;
    const nextNotes = resolvePromptText(notesOverride, notes);
    setNotes(nextNotes);
    const nextImagePrompt = await generateCopy(nextNotes);
    if (nextImagePrompt) await generatePostImage(nextImagePrompt);
  };

  const copyPostToClipboard = async () => {
    const text = fullPostText.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError(
        t("marketing.copyError", "Could not copy to clipboard. Please copy manually.")
      );
    }
  };

  usePendingTask("/marketing", (task) => {
    setTaskBanner(task.rawPrompt || task.prompt);
    setNotes(task.prompt || "");
    generateAll(task.prompt || "");
  });

  const taskDone = useHomeTaskDone(busy, error, Boolean(taskBanner));

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader
        icon={Megaphone}
        title={t("nav.marketing", "Marketing")}
        subtitle={t(
          "marketing.subtitle",
          "Create social post copy with chat AI and matching visuals in one workflow."
        )}
      />

      <TaskBanner
        label={t("nav.marketing", "Marketing")}
        prompt={taskBanner}
        running={busy}
        error={error}
        completed={taskDone}
      />

      <GenerationProgress
        active={busy}
        message={status}
        hint={t(
          "marketing.hint",
          "Copy usually streams in seconds. Images may take 5–30 seconds."
        )}
        className="mx-4 md:mx-10 mt-3"
      />

      <div className="flex-1 px-4 md:px-10 py-6 grid lg:grid-cols-[390px,1fr] gap-6 max-w-7xl mx-auto w-full">
        <aside className="card space-y-4 h-fit">
          <div>
            <label className="label">{t("marketing.business", "Business")}</label>
            <input
              value={business}
              onChange={(e) => setBusiness(e.target.value)}
              placeholder={t("marketing.businessPlaceholder", "e.g. Pine & Stone Dental")}
              className="input"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) =>
                setBusiness((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />
          </div>

          <div>
            <label className="label">{t("marketing.offer", "Offer / Service")}</label>
            <input
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
              placeholder={t(
                "marketing.offerPlaceholder",
                "e.g. New patient cleaning special"
              )}
              className="input"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) =>
                setOffer((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />
          </div>

          <div>
            <label className="label">{t("marketing.audience", "Target audience")}</label>
            <input
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder={t(
                "marketing.audiencePlaceholder",
                "e.g. Busy parents in Austin"
              )}
              className="input"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) =>
                setAudience((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{t("marketing.platform", "Platform")}</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="input"
              >
                {PLATFORM_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">{t("marketing.tone", "Tone")}</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="input"
              >
                {TONE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label">{t("marketing.goal", "Goal")}</label>
            <input
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t(
                "marketing.goalPlaceholder",
                "e.g. Book appointments this week"
              )}
              className="input"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) =>
                setGoal((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />
          </div>

          <div>
            <label className="label">{t("marketing.notes", "Extra notes")}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t(
                "marketing.notesPlaceholder",
                "Any promo details, brand voice notes, or seasonal angle"
              )}
              className="textarea min-h-[90px]"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) =>
                setNotes((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{t("marketing.chatModel", "Chat model")}</label>
              <select
                value={chatModel}
                onChange={(e) => setChatModel(e.target.value)}
                className="input"
              >
                {chatModels.length === 0 && (
                  <option value={chatModel}>{chatModel}</option>
                )}
                {chatModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">{t("marketing.imageModel", "Image model")}</label>
              <select
                value={imageModel}
                onChange={(e) => setImageModel(e.target.value)}
                className="input"
              >
                {imageModels.length === 0 && (
                  <option value={imageModel}>{imageModel}</option>
                )}
                {imageModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              className="btn-secondary"
              disabled={!canGenerateCopy}
              onClick={() => generateCopy()}
            >
              {loadingCopy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {t("marketing.generateCopy", "Generate copy")}
            </button>
            <button
              className="btn-primary"
              disabled={!canGenerateCopy || busy}
              onClick={() => generateAll()}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
              {t("marketing.generateAll", "Generate post + image")}
            </button>
          </div>
        </aside>

        <section className="space-y-4 min-w-0">
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold">
                {t("marketing.postDraft", "Post draft")}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  className="btn-secondary"
                  onClick={copyPostToClipboard}
                  disabled={!fullPostText.trim()}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied
                    ? t("home.share.copied", "Copied")
                    : t("marketing.copyPost", "Copy post")}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => generateCopy()}
                  disabled={!canGenerateCopy}
                >
                  <RefreshCw className="h-4 w-4" />
                  {t("marketing.regenerateCopy", "Regenerate copy")}
                </button>
              </div>
            </div>

            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t(
                "marketing.captionPlaceholder",
                "Your generated social caption will appear here."
              )}
              className="textarea min-h-[200px]"
            />
            <SpeechToTextButton
              onTranscript={(text) =>
                setCaption((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />

            <div>
              <label className="label">{t("marketing.hashtags", "Hashtags")}</label>
              <input
                value={hashtags.join(" ")}
                onChange={(e) =>
                  setHashtags(
                    e.target.value
                      .split(/\s+/)
                      .map((tag) => tag.trim())
                      .filter(Boolean)
                  )
                }
                placeholder={t(
                  "marketing.hashtagsPlaceholder",
                  "#smallbusiness #localbusiness #marketing"
                )}
                className="input"
              />
              <SpeechToTextButton
                className="mt-2"
                onTranscript={(text) =>
                  setHashtags((prev) => [
                    ...prev,
                    ...text
                      .split(/\s+/)
                      .map((tag) => tag.trim())
                      .filter(Boolean),
                  ])
                }
                onError={(msg) => setError(msg)}
              />
            </div>
          </div>

          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold">
                {t("marketing.creativeImage", "Creative image")}
              </h3>
              <div className="flex items-center gap-2">
                {imageSrc && (
                  <a
                    href={imageSrc}
                    download={`marketing-post-${Date.now()}.png`}
                    className="btn-secondary"
                  >
                    <Download className="h-4 w-4" />
                    {t("common.download", "Download")}
                  </a>
                )}
                <button
                  className="btn-secondary"
                  onClick={generatePostImage}
                  disabled={!canGenerateImage}
                >
                  {loadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImageIcon className="h-4 w-4" />
                  )}
                  {t("marketing.generateImage", "Generate image")}
                </button>
              </div>
            </div>

            <textarea
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder={t(
                "marketing.imagePromptPlaceholder",
                "Image concept prompt for your post"
              )}
              className="textarea min-h-[90px]"
            />
            <SpeechToTextButton
              onTranscript={(text) =>
                setImagePrompt((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />

            {imageSrc ? (
              <img
                src={imageSrc}
                alt="Marketing social post visual"
                className="w-full h-auto rounded-xl border border-white/10"
              />
            ) : (
              <div className="rounded-xl border border-dashed border-white/15 px-4 py-10 text-center text-white/45 text-sm">
                {t("marketing.imageOutputPlaceholder", "Generated post image appears here.")}
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              {error}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
